// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import { formatDate } from '@loa/theme';
import { useNavigate } from 'react-router-dom';
import { Alert, AppIcon, Button, Spinner, Tooltip } from '@loa/ui-components';
import { invitationApi } from '../api/invitationApi';
import { profileFormApi } from '../api/profileFormApi';
import type { Client } from '../types/client';
import type { InvitationStatus, InvitationStatusResponse } from '../types/invitation';
import './ClientList.css';

type ClientListProps = Readonly<{
  clients: Client[];
  loading: boolean;
  error: string | null;
  onEdit: (client: Client) => void;
  onArchive: (client: Client) => void;
  onSetPrimary: (clientId: string) => void;
  onInvitationUpdate?: () => void;
  onRowClick?: (client: Client) => void;
  readOnly?: boolean;
}>;

export function ClientList({
  clients,
  loading,
  error,
  onEdit,
  onArchive,
  onRowClick,
  readOnly = false
}: ClientListProps) {
  const navigate = useNavigate();
  const [invitationStatuses, setInvitationStatuses] = useState<Record<string, InvitationStatus>>({});
  // Per-client assessments (Risk / Sustainability / Deep Dive): each client has
  // their own record — completion is keyed by clientId.
  const [riskComplete, setRiskComplete] = useState<Record<string, boolean>>({});
  const [sustainabilityComplete, setSustainabilityComplete] = useState<Record<string, boolean>>({});
  const [deepDiveComplete, setDeepDiveComplete] = useState<Record<string, boolean>>({});

  const loadInvitationStatuses = useCallback(async () => {
    const statuses: Record<string, InvitationStatus> = {};

    await Promise.all(
      clients.map(async (client) => {
        try {
          const apiStatus: InvitationStatusResponse = await invitationApi.getInvitationStatus(client.id);

          let mappedStatus: 'not_invited' | 'invited' | 'registered' | 'signed';
          const statusLower = apiStatus.status?.toLowerCase();
          if (statusLower === 'prospect' || statusLower === 'draft') {
            mappedStatus = 'not_invited';
          } else if (statusLower === 'invited') {
            mappedStatus = 'invited';
          } else if (statusLower === 'active') {
            mappedStatus = 'registered';
          } else if (statusLower === 'signed') {
            mappedStatus = 'signed';
          } else {
            mappedStatus = apiStatus.invitedAt ? 'invited' : 'not_invited';
          }

          statuses[client.id] = {
            ...apiStatus,
            status: mappedStatus
          };
        } catch (err) {
          console.error(`Failed to load invitation status for ${client.id}:`, err);
        }
      })
    );

    setInvitationStatuses(statuses);
  }, [clients]);

  useEffect(() => {
    if (!readOnly) {
      loadInvitationStatuses();
    }
  }, [loadInvitationStatuses, readOnly]);

  const loadRiskStatuses = useCallback(async () => {
    // Per-client assessments: one lookup per CLIENT per type — a submitted
    // version (getLatest !== null) means THAT client's assessment is complete.
    const risk: Record<string, boolean> = {};
    const sustainability: Record<string, boolean> = {};
    const deepDive: Record<string, boolean> = {};

    await Promise.all(
      clients.map(async (client) => {
        try {
          const [riskLatest, susLatest, ddLatest] = await Promise.all([
            profileFormApi.getLatest(client.profileId, 'RISK_ATTITUDE', client.id),
            profileFormApi.getLatest(client.profileId, 'SUSTAINABILITY', client.id),
            profileFormApi.getLatest(client.profileId, 'DEEP_DIVE', client.id),
          ]);
          risk[client.id] = riskLatest !== null;
          sustainability[client.id] = susLatest !== null;
          deepDive[client.id] = ddLatest !== null;
        } catch (err) {
          console.error(`Failed to load assessment status for client ${client.id}:`, err);
          risk[client.id] = false;
          sustainability[client.id] = false;
          deepDive[client.id] = false;
        }
      })
    );

    setRiskComplete(risk);
    setSustainabilityComplete(sustainability);
    setDeepDiveComplete(deepDive);
  }, [clients]);

  useEffect(() => {
    if (!readOnly) {
      loadRiskStatuses();
    }
  }, [loadRiskStatuses, readOnly]);

  const formatEnum = (value?: string): string => {
    if (!value) return '-';
    return value.charAt(0) + value.slice(1).toLowerCase().replaceAll('_', ' ');
  };

  const getFullAddress = (client: Client): string => {
    const parts = [
      client.addressLine1,
      client.addressLine2,
      client.addressLine3,
      [client.townCity, client.stateProvinceCounty].filter(Boolean).join(' '),
      client.postalCode,
    ].filter((p) => p && p.length > 0);
    return parts.length > 0 ? parts.join(', ') : '-';
  };

  const handleResendInvitation = (clientId: string, profileId: string) => {
    navigate(`/resend-invitation?profileId=${profileId}&clientId=${clientId}&mode=resend`);
  };

  if (loading) {
    return (
      <div className="client-list-loading">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="client-list-error">
        <Alert variant="error" header="Error Loading Clients">
          <p>{error}</p>
        </Alert>
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className="client-list-empty">
        <h4 className="client-list-empty__title">No Clients Found</h4>
        <p className="client-list-empty__description">Add a client to this profile to get started.</p>
      </div>
    );
  }

  const getDisplayStatus = (client: Client): string => {
    if (client.status === 'inactive') return 'archived';
    const invitationStatus = invitationStatuses[client.id];
    if (!invitationStatus) return 'not_invited';
    if (invitationStatus.status === 'not_invited') return 'not_invited';
    if (invitationStatus.status === 'invited') return 'invite_sent';
    if (invitationStatus.status === 'registered' || invitationStatus.status === 'signed') return 'active';
    return 'not_invited';
  };

  const renderStatusTag = (status: string) => {
    switch (status) {
      case 'invite_sent':
        return (
          <span className="client-status-tag client-status-tag--invite-sent">
            <AppIcon name="envelope" size={16} className="client-status-tag__icon" />
            Invite sent
          </span>
        );
      case 'active':
        return (
          <span className="client-status-tag client-status-tag--active">
            <AppIcon name="checkbox" size={16} className="client-status-tag__icon" />
            Active
          </span>
        );
      case 'archived':
        return (
          <span className="client-status-tag client-status-tag--archived">
            <AppIcon name="archive box" size={16} className="client-status-tag__icon" />
            Archived
          </span>
        );
      case 'not_invited':
      default:
        return (
          <span className="client-status-tag client-status-tag--not-invited">
            <AppIcon name="invite not sent" size={16} className="client-status-tag__icon" />
            Not invited
          </span>
        );
    }
  };

  const renderActions = (client: Client, displayStatus: string) => {
    if (readOnly) {
      return (
        <span className="client-view-only">
          <AppIcon name="eye" size={14} />
          View Only
        </span>
      );
    }

    if (displayStatus === 'archived') {
      return null;
    }

    return (
      <div className="flex items-center gap-md">
        <Button
          variant="outlined"
          colour="purple"
          size="md"
          onClick={() => navigate(`/clients/${client.id}/aml`)}
        >
          AML
        </Button>

        <Button
          variant="outlined"
          colour="purple"
          size="md"
          iconLeft={
            riskComplete[client.id]
              ? <AppIcon name="checkmark circle" size={16} color="#2D0A6C" />
              : undefined
          }
          disabled={!riskComplete[client.id]}
          onClick={() => {
            if (riskComplete[client.id]) {
              navigate(`/clients/${client.id}/risk`, { state: { profileId: client.profileId } });
            }
          }}
        >
          Risk
        </Button>

        <Button
          variant="outlined"
          colour="purple"
          size="md"
          iconLeft={
            sustainabilityComplete[client.id]
              ? <AppIcon name="checkmark circle" size={16} color="#2D0A6C" />
              : undefined
          }
          disabled={!sustainabilityComplete[client.id]}
          onClick={() => {
            if (sustainabilityComplete[client.id]) {
              navigate(`/clients/${client.id}/sustainability`, { state: { profileId: client.profileId } });
            }
          }}
        >
          Sustainability
        </Button>

        <Button
          variant="outlined"
          colour="purple"
          size="md"
          iconLeft={
            deepDiveComplete[client.id]
              ? <AppIcon name="checkmark circle" size={16} color="var(--color-purple-primary)" />
              : undefined
          }
          disabled={!deepDiveComplete[client.id]}
          onClick={() => {
            if (deepDiveComplete[client.id]) {
              navigate(`/clients/${client.id}/deep-dive`, { state: { profileId: client.profileId } });
            }
          }}
        >
          Deep Dive
        </Button>

        <Tooltip
          content="Edit client"
          trigger={
            <button
              className="flex w-[40px] h-[40px] justify-center items-center shrink-0 rounded-lg border border-purple-primary bg-transparent transition-colors hover:bg-[var(--btn-outlined-hover-bg)] cursor-pointer focus:outline-none focus-visible:outline-none"
              onClick={() => onEdit(client)}
              aria-label="Edit client"
            >
              <AppIcon name="edit title" size={24} color="#2D0A6C" />
            </button>
          }
        />

        {displayStatus === 'invite_sent' && (
          <Tooltip
            content="Resend invitation email"
            trigger={
              <button
                className="flex w-[32px] h-[32px] p-sm justify-center items-center shrink-0 rounded-full bg-lilac-05 border-none cursor-pointer focus:outline-none focus-visible:outline-none"
                onClick={() => handleResendInvitation(client.id, client.profileId)}
                aria-label="Resend invitation"
              >
                <AppIcon name="refresh" size={16} color="#2D0A6C" />
              </button>
            }
          />
        )}

        {!client.isPrimary && (
          <Tooltip
            content="Archive client"
            trigger={
              <button
                className="flex w-[40px] h-[40px] justify-center items-center shrink-0 rounded-lg border border-purple-primary bg-transparent transition-colors hover:bg-[var(--btn-outlined-hover-bg)] cursor-pointer focus:outline-none focus-visible:outline-none"
                onClick={() => onArchive(client)}
                aria-label="Archive client"
              >
                <AppIcon name="archive drawer" size={24} color="#2D0A6C" />
              </button>
            }
          />
        )}
      </div>
    );
  };

  const labelClass = "text-[#000] font-sans text-body-02 font-medium leading-normal break-words";
  const valueClass = "text-[#000] font-sans text-body-02 font-normal leading-normal break-words";

  const renderField = (label: string, value: string) => (
    <div className="flex flex-col gap-sm">
      <div className={labelClass}>{label}</div>
      <div className={valueClass}>{value || '-'}</div>
    </div>
  );

  return (
    <div className="flex flex-col gap-lg">
      {clients.map((client) => {
        const displayStatus = getDisplayStatus(client);

        return (
          <div key={client.id} className="rounded-card shadow-[0_0_10px_0_rgba(0,0,0,0.10)] bg-white">
            {/* Header: name + status badges + actions */}
            <div className="flex items-center justify-between gap-md px-md py-lg">
              <div
                className={`flex items-center gap-md flex-wrap ${onRowClick ? 'cursor-pointer' : ''}`}
                onClick={() => onRowClick?.(client)}
              >
                <span className="text-black font-sans text-body-01 font-medium leading-normal">
                  {client.firstName} {client.lastName}
                </span>
                <span className="flex px-sm py-xs rounded-[4px] bg-[#EFEFFF] text-purple-primary font-sans text-body-03 font-medium leading-normal">
                  {client.isPrimary ? 'Primary' : 'Secondary'}
                </span>
                {renderStatusTag(displayStatus)}
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                {renderActions(client, displayStatus)}
              </div>
            </div>

            {/* Details: bordered bottom section */}
            <div className="flex flex-wrap justify-between gap-lg border-t border-[#E1E1E1] bg-white px-lg py-lg rounded-b-[16px]">
              {renderField('Email', client.email)}
              {renderField('Phone', client.phone)}
              {renderField('Date of Birth', formatDate(client.dateOfBirth))}
              {renderField('Gender', formatEnum(client.gender))}
              {renderField('Marital Status', formatEnum(client.maritalStatus))}
              {renderField('Address', getFullAddress(client))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
